import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField,
  Slider,
  Alert,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/system';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import BrushIcon from '@mui/icons-material/Brush';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
// Removed Gemini import and initialization from here

const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 120,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.spacing(1.5),
  marginTop: theme.spacing(1)
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& .MuiAlert-icon': {
    alignSelf: 'flex-start',
    marginTop: '4px'
  }
}));

const promptExamples = [
  { value: 'empty_wall', label: 'Boş duvar ile değiştir', prompt: 'Bu alanı temiz ve boş bir duvar ile değiştir' },
  { value: 'window', label: 'Pencere ekle', prompt: 'Bu alanı büyük, modern bir pencere ile değiştir, dışarıda güzel bir manzara olsun' },
  { value: 'door', label: 'Kapı ekle', prompt: 'Bu alanı ahşap bir kapı ile değiştir' },
  { value: 'furniture_remove', label: 'Mobilyayı kaldır', prompt: 'Bu mobilyayı tamamen kaldır ve arkasındaki duvarı/zemini doğal şekilde tamamla' },
  { value: 'plant', label: 'Bitki ekle', prompt: 'Bu alanı güzel bir iç mekan bitkisi ile değiştir' },
  { value: 'artwork', label: 'Sanat eseri ekle', prompt: 'Bu alanı modern bir sanat eseri/tablo ile değiştir' },
];

const MagicEraseToolOptions = ({ settings, onChange, onApply, maskUrl }) => {
  const [previewStyle, setPreviewStyle] = useState({});
  const [selectedExample, setSelectedExample] = useState('');
  
  const handlePromptChange = (event) => {
    onChange({ ...settings, prompt: event.target.value });
  };
  
  const handleBrushSizeChange = (event, newValue) => {
    onChange({ ...settings, brushSize: newValue });
  };
  
  const handleExampleSelect = (event) => {
    const selected = event.target.value;
    setSelectedExample(selected);
    
    if (selected) {
      const example = promptExamples.find(ex => ex.value === selected);
      if (example) {
        onChange({ ...settings, prompt: example.prompt });
      }
    }
  };
  
  const handleReset = () => {
    onChange({ ...settings, prompt: '', brushSize: 20 });
    setSelectedExample('');
  };

  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Stack spacing={2}>
          <Box>
            <OptionTitle>
              <AutoFixHighIcon /> Sihirli Değnek
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              İstenmeyen öğeleri kaldırın veya seçilen alanları değiştirin
            </Typography>
          </Box>
          
          <PreviewBox>
            {!maskUrl ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                p: 2, 
                textAlign: 'center' 
              }}>
                <BrushIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, opacity: 0.7 }} />
                <Typography variant="body2" color="text.secondary">
                  Maskeleme işlemi için önce "Maske Oluştur" düğmesine tıklayın
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                p: 2, 
                textAlign: 'center',
                width: '100%' 
              }}>
                <CheckCircleOutlineIcon sx={{ 
                  fontSize: 40, 
                  color: 'success.main', 
                  mb: 1,
                  animation: 'pulse 1.5s infinite ease-in-out',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                    '50%': { transform: 'scale(1.05)', opacity: 1 },
                    '100%': { transform: 'scale(0.95)', opacity: 0.7 },
                  }
                }} />
                <Typography variant="body2" color="success.main" fontWeight="medium">
                  Maske başarıyla oluşturuldu!
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Şimdi isteğinizi yazabilir veya hazır örneklerden seçebilirsiniz
                </Typography>
              </Box>
            )}
          </PreviewBox>
          
          {!maskUrl ? (
            <StyledAlert 
              severity="info" 
              icon={<InfoOutlinedIcon />}
              sx={{ 
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(30, 64, 175, 0.15)' : 'rgba(229, 246, 253, 0.85)'
              }}
            >
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>Maskeleme Adımları:</Typography>
              <ol style={{ margin: '0', paddingLeft: '20px' }}>
                <li>Değiştirmek istediğiniz alanı işaretlemek için fırça aracını kullanın</li>
                <li>Fırça boyutunu ayarlamak için kaydırıcıyı kullanın</li>
                <li>Görünümü kaydırmak için sağdaki kaydırma aracını kullanın</li>
                <li>İşiniz bittiğinde "Kaydet" düğmesine tıklayın</li>
              </ol>
            </StyledAlert>
          ) : (
            <>
              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                <InputLabel id="prompt-example-label">Hazır İstek Örnekleri</InputLabel>
                <Select
                  labelId="prompt-example-label"
                  id="prompt-example-select"
                  value={selectedExample}
                  label="Hazır İstek Örnekleri"
                  onChange={handleExampleSelect}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '8px'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Seçiniz</em>
                  </MenuItem>
                  {promptExamples.map((example) => (
                    <MenuItem key={example.value} value={example.value}>
                      {example.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  <TextFormatIcon fontSize="small" sx={{ mr: 1 }} />
                  İstek (isteğe bağlı)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Maskelenen alanı nasıl değiştirmek istediğinizi açıklayın..."
                  value={settings.prompt}
                  onChange={handlePromptChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mt: 1, 
                  p: 1, 
                  borderRadius: '8px',
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.1)' : 'rgba(138, 43, 226, 0.05)',
                  border: '1px dashed rgba(138, 43, 226, 0.3)'
                }}>
                  <AutoFixHighIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Türkçe yazabilirsiniz - isteğiniz otomatik olarak İngilizce'ye çevrilecektir
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Örnek: "Bu alanı boş duvar ile değiştir" veya "Bu alanı pencere ile değiştir"
                </Typography>
              </Box>
            </>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, gap: 2 }}>
            {maskUrl ? (
              <>
                <Button 
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RestartAltIcon />}
                  sx={{ flex: 1 }}
                >
                  Sıfırla
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => onApply(true)}
                  disabled={!maskUrl}
                  sx={{ 
                    flex: 2,
                    background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                    boxShadow: '0 3px 5px 2px rgba(138, 43, 226, .3)'
                  }}
                >
                  YZ Uygula (10 Kredi)
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => onApply(false)}
                fullWidth
                sx={{ 
                  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                  boxShadow: '0 3px 5px 2px rgba(138, 43, 226, .3)'
                }}
              >
                Maske Oluştur
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
    </OptionsPaper>
  );
};

export default MagicEraseToolOptions;
